import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, ArrowUpRight, Users2, Briefcase, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminCompanies() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const downloadUsageReport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/usage-report");
      const { rows, summary: s } = r.data;

      // Sheet 1: Session Detail
      const sessionRows = rows.map((r) => ({
        "Participant Name": r.participant_name,
        "Email": r.participant_email,
        "Employee ID": r.employee_id,
        "Company": r.company,
        "Batch": r.batch,
        "Program": r.program,
        "Scenario": r.scenario,
        "Category": r.category,
        "Difficulty": r.difficulty,
        "Session Start": r.started_at ? new Date(r.started_at).toLocaleString() : "—",
        "Session End": r.ended_at ? new Date(r.ended_at).toLocaleString() : "—",
        "Duration (min)": r.duration_min ?? "—",
        "Text Messages": r.text_messages,
        "Voice Messages": r.voice_messages,
        "Total User Messages": r.total_user_messages,
        "AI Responses": r.ai_responses,
        "Total Words Sent": r.total_words_sent,
        "Total Chars Sent": r.total_chars_sent,
        "Est. Tokens (chars÷4)": r.est_tokens,
        "Completed": r.completed ? "Yes" : "No",
        "Attempt #": r.attempt_number,
        "Overall Score": r.overall_score ?? "—",
      }));

      // Sheet 2: Summary
      const summaryRows = [
        ["Metric", "Value", "Notes"],
        ["Total Participants", s.total_participants, "Unique users with at least one session"],
        ["Total Sessions", s.total_sessions, ""],
        ["Completed Sessions", s.completed_sessions, ""],
        ["Completion Rate (%)", s.completion_rate, ""],
        ["Unique Scenarios Attempted", s.total_unique_scenarios, ""],
        ["Avg Sessions per Participant", s.avg_sessions_per_participant, ""],
        ["", "", ""],
        ["Avg Duration (min)", s.avg_duration_min, "Completed sessions only"],
        ["Median Duration (min)", s.median_duration_min, ""],
        ["P75 Duration (min)", s.p75_duration_min, ""],
        ["P90 Duration (min)", s.p90_duration_min, ""],
        ["", "", ""],
        ["Avg Text Messages / Session", s.avg_text_messages, "Completed sessions only"],
        ["Avg Voice Messages / Session", s.avg_voice_messages, "Tracked from deploy date only"],
        ["Avg AI Responses / Session", s.avg_ai_interactions, ""],
        ["Avg Est. Tokens / Session", s.avg_est_tokens, "Estimated: total chars ÷ 4"],
        ["", "", ""],
        ["Text Usage (%)", s.pct_text, "Share of messages sent via text"],
        ["Voice Usage (%)", s.pct_voice, "Share of messages sent via voice"],
      ];

      const wb = XLSX.utils.book_new();

      const ws1 = XLSX.utils.json_to_sheet(sessionRows);
      ws1["!cols"] = [
        { wch: 22 }, { wch: 30 }, { wch: 14 }, { wch: 20 }, { wch: 22 },
        { wch: 22 }, { wch: 42 }, { wch: 28 }, { wch: 12 }, { wch: 22 },
        { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 },
        { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 22 }, { wch: 12 },
        { wch: 10 }, { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, "Session Detail");

      const ws2 = XLSX.utils.aoa_to_sheet(summaryRows);
      ws2["!cols"] = [{ wch: 38 }, { wch: 14 }, { wch: 48 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Summary");

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `leadersim-usage-report-${today}.xlsx`);
      toast.success(`Downloaded — ${rows.length} sessions`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const load = () => {
    setLoading(true);
    api
      .get("/admin/companies")
      .then((r) => setCompanies(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/admin/companies", { name, logo_url: logo || null });
      toast.success("Company created");
      setOpen(false);
      setName("");
      setLogo("");
      navigate(`/admin/companies/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-2">Administration</div>
            <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">Companies</h1>
            <p className="mt-1 text-stone-600 text-sm">Create a company, then add batches and participants underneath.</p>
          </div>
          {user?.role === "admin" && (
            <div className="flex items-center gap-2">
              <Button
                data-testid="download-usage-report-btn"
                variant="outline"
                onClick={downloadUsageReport}
                disabled={exporting}
                className="rounded-md h-10 px-5 border-stone-300"
              >
                <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                {exporting ? "Preparing…" : "Usage Report (Excel)"}
              </Button>
              <Button
                data-testid="new-company-btn"
                onClick={() => setOpen(true)}
                className="rounded-md bg-[#b81f24] hover:bg-[#9a1a1f] text-white h-10 px-5"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                New company
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-stone-500 text-sm">Loading…</div>
        ) : companies.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center">
            <Building2 className="w-10 h-10 text-stone-300 mx-auto mb-4" strokeWidth={1.25} />
            <div className="font-display text-xl text-stone-900">No companies yet</div>
            <p className="text-stone-500 mt-1 text-sm">Create your first company to start running batches.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
            {companies.map((c) => (
              <Link
                key={c.id}
                to={`/admin/companies/${c.id}`}
                data-testid={`company-card-${c.id}`}
                className="group bg-white border border-stone-200 rounded-3xl p-6 hover:border-[#b81f24]/30 hover:shadow-[0_18px_50px_-24px_rgba(28,25,23,0.18)] transition-all"
              >
                <div className="flex items-center gap-4 mb-5">
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="w-14 h-14 rounded-2xl object-cover bg-stone-100 border border-stone-200" />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-stone-100 border border-stone-200 grid place-items-center">
                      <Building2 className="w-6 h-6 text-stone-500" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-stone-900 truncate">{c.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">Created {new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-stone-400 group-hover:text-[#b81f24] transition-colors" strokeWidth={1.75} />
                </div>
                <div className="flex gap-3 text-xs text-stone-600">
                  <span className="inline-flex items-center gap-1.5 bg-stone-100 rounded-full px-3 py-1">
                    <Briefcase className="w-3 h-3" strokeWidth={1.5} /> {c.batches_count} batches
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-stone-100 rounded-full px-3 py-1">
                    <Users2 className="w-3 h-3" strokeWidth={1.5} /> {c.participants_count} people
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">New company</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Company name *</Label>
              <Input
                data-testid="company-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Acme Corp"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Logo URL (optional)</Label>
              <Input
                data-testid="company-logo-input"
                value={logo}
                onChange={(e) => setLogo(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                data-testid="save-company-btn"
                type="submit"
                disabled={saving}
                className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white"
              >
                {saving ? "Saving…" : "Create company"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
